// packages
import dayjs from 'dayjs';

// types
import { View as PeriodView, SlotInfo } from 'react-big-calendar';
import { Period } from 'components/shared/DateNavigation/DateNavigation.types';
import {
  AvailabilityType,
  EventResponse,
  PlanningEntityIdentifier,
  PlanningEntityResponse,
  PlanningEntityType
} from 'generated/index';
import { CalendarSourceData } from 'components/mrp/stores/calendarStore/calendarStore.types';
import {
  CalendarAppointmentEvent,
  CalendarAvailabilityEvent
} from './Calendar.types';

export const planningPeriodToBigCalendarPeriod = (
  period: Period | null,
  defaultView: PeriodView = 'week'
): PeriodView => {
  if (!period) return defaultView;
  if (period === 'workweek') return 'work_week';
  if (['day', 'week', 'work_week', 'month'].includes(period)) {
    return period as PeriodView;
  }
  return defaultView;
};

export const createTabTitleFromEntities = (
  entities: PlanningEntityResponse[]
) => {
  if (entities.length === 0) return '...';
  return entities[0].name;
};

export const createCalendarAppointmentEventFromPlanningEvent = (
  calendarId: string | undefined = '',
  event: EventResponse
) => {
  const { id, start, end, activity } = event;

  return {
    calendarId,
    id,
    end: dayjs(end).toDate(),
    start: dayjs(start).toDate(),
    title: activity?.code || activity?.name || '',
    planningEvent: event
  } satisfies CalendarAppointmentEvent;
};

type createCalendarAppointmentEventFromPlanningEventBuilderOptions = {
  titleBuilder?: (event: EventResponse) => string | undefined;
};

export const createCalendarAppointmentEventFromPlanningEventBuilder = (
  options?: createCalendarAppointmentEventFromPlanningEventBuilderOptions
) => {
  return (event: EventResponse) => {
    const { id, start, end, activity } = event;

    let eventTitle = activity?.name || '';

    if (options?.titleBuilder) {
      // Overrides the default title with the one from the builder. If
      // the builder output is null, it'll remain unchanged from the default.
      eventTitle = options.titleBuilder(event) || eventTitle;
    }

    return {
      id,
      end: dayjs(end).toDate(),
      start: dayjs(start).toDate(),
      title: eventTitle,
      planningEvent: event
    };
  };
};

export const createEventResponseFromSlotInfo = (
  slotInfo: SlotInfo,
  calendarSources: CalendarSourceData[]
) => {
  const { start, end } = slotInfo;

  if (!start) return {};

  const endActual = end || dayjs().add(30, 'minute').toDate();

  return {
    start: start.toISOString(),
    end: endActual.toISOString(),
    ...(calendarSources || [])
      .map(
        ({
          label,
          planningEntityIdentifier: { entityId, planningEntityType, number }
        }) => ({
          entityId,
          number,
          label,
          planningEntityType
        })
      )
      .reduce((acc, { entityId, planningEntityType, number, label }) => {
        const isWorkPlace = planningEntityType === 'WORKPLACE';

        const entity = {
          id: entityId,
          entityId,
          number,
          name: label,
          displayName: label,
          nameInformal: label
        };

        if (isWorkPlace) {
          acc['workPlace'] = entity;
        } else {
          acc[`${planningEntityType.toLowerCase()}s`] = [
            ...(acc[`${planningEntityType.toLowerCase()}s`] || []),
            entity
          ];
        }

        return acc;
      }, {})
  } as Partial<EventResponse>;
};

export const createPlanningEntityIdentifiersFromEventResponse = (
  event: EventResponse
): Pick<PlanningEntityIdentifier, 'entityId'>[] => {
  const {
    clients = [],
    employees = [],
    resources = [],
    workPlace = {}
  } = event;

  return (
    [...clients, ...employees, ...resources, workPlace] as {
      entityId?: string;
      id?: string;
    }[]
  )
    .filter((entity) => !!entity)
    .map((entity) => ({
      entityId: entity.entityId || entity?.id || ''
    })) as Pick<PlanningEntityIdentifier, 'entityId'>[];
};

export const createCalendarEventsFromPlanningEntityResponse = (
  calendarId: string | undefined = '',
  planningEntities: PlanningEntityResponse[]
) => {
  return planningEntities
    .flatMap(({ schedules, events }) =>
      schedules.flatMap(({ events }) => events).concat(events)
    )
    .filter(({ category }) => category !== 'FREE' && category !== 'TRAVELTIME')
    .map((event) => ({
      ...createCalendarAppointmentEventFromPlanningEvent(calendarId, event)
    })) as CalendarAppointmentEvent[];
};

const mapAvailabilityEvent = ({
  entityId,
  start,
  end,
  calendarId = '',
  type,
  entitySourceType
}: {
  entityId?: string;
  start?: string;
  end?: string;
  calendarId: string | undefined;
  type?: AvailabilityType | 'DUTY';
  entitySourceType: PlanningEntityType;
}): CalendarAvailabilityEvent => ({
  entityId,
  start: new Date(start!),
  end: new Date(end!),
  calendarId,
  type: type!,
  entitySourceType
});

const getAvailabilityEvents = (
  calendarId: string | undefined = '',
  planningEntities: PlanningEntityResponse[]
) => {
  const availabilityEvents: CalendarAvailabilityEvent[] = [];

  if (
    planningEntities.some(
      ({ planningEntityIdentifier: { planningEntityType } }) =>
        planningEntityType === 'EMPLOYEE' || planningEntityType === 'EMPLOYMENT'
    )
  ) {
    // For employees, we only use "NOT_AVAILABLE" from the
    // general set of availabilities. For "AVAILABLE" and
    // "POSSIBLE", we need to reference the schedules.

    // const unavailabilities = planningEntities
    //   .filter((x) => !!x.availabilities)
    //   .flatMap(({ availabilities }) => availabilities)
    //   .filter((availability) => availability!.type == 'NOT_AVAILABLE')
    //   .map((availability) =>
    //     mapAvailabilityEvent({
    //       ...availability,
    //       calendarId,
    //       entitySourceType: 'EMPLOYEE'
    //     })
    //   );

    // Absences are considered as "NOT_AVAILABLE" time blocks for employees,
    // so we need to include them as well.
    const absences = planningEntities
      .filter((x) => !!x.absences)
      .flatMap(({ absences }) => absences)
      .map((absence) =>
        mapAvailabilityEvent({
          ...absence,
          calendarId,
          type: 'NOT_AVAILABLE',
          entitySourceType: 'EMPLOYEE'
        })
      );

    const dutyAvailabilities = planningEntities
      .flatMap(({ schedules }) => schedules)
      .filter(({ type, duty }) => type == 'DUTY' && !!duty)
      .map(({ duty }) => ({
        ...mapAvailabilityEvent({
          ...duty,
          calendarId,
          type: 'DUTY',
          entitySourceType: 'EMPLOYEE'
        }),
        label: [
          // TODO: To be decided and sent by BE what needs to be displayed in the label.
          duty?.dutyCode?.code,
          duty?.periods?.[0]?.dutyHourType?.code
        ]
          .filter(Boolean)
          .join(' / ')
      }));

    // TODO: the ui employeeAvailabilities needs to be decided. So left out for now
    // const employeeAvailabilities = planningEntities
    //   .flatMap(({ schedules }) => schedules)
    //   .filter(
    //     ({ type, mainAvailability }) =>
    //       type == 'AVAILABILITY' && !!mainAvailability
    //   )
    //   .map(({ mainAvailability }) =>
    //     mapAvailabilityEvent({
    //       ...mainAvailability,
    //       calendarId,
    //       entitySourceType: 'EMPLOYEE'
    //     })
    //   );

    // availabilityEvents.push(...unavailabilities);
    availabilityEvents.push(...absences);
    availabilityEvents.push(...dutyAvailabilities);
    // TODO: the ui employeeAvailabilities needs to be decided. So left out for now
    // availabilityEvents.push(...employeeAvailabilities);
  } else {
    // For non-employee entities, we can simply use the
    // general availabilities for all types.
    planningEntities
      .filter((x) => !!x.availabilities)
      .forEach(({ availabilities, type }) => {
        availabilityEvents.push(
          ...availabilities!.map((availability) =>
            mapAvailabilityEvent({
              ...availability,
              calendarId,
              entitySourceType: type
            })
          )
        );
      });
  }

  return availabilityEvents;
};

const mergeAvailabilityEvents = (events: CalendarAvailabilityEvent[]) => {
  const groupedByCalendarAndType = events.reduce(
    (acc, event) => {
      const key = `${event.calendarId}-${event.type}-${event.label ?? ''}`;
      acc[key] = [...(acc[key] || []), event];

      return acc;
    },
    {} as Record<string, CalendarAvailabilityEvent[]>
  );

  const merged: CalendarAvailabilityEvent[] = [];

  Object.values(groupedByCalendarAndType).forEach((events) => {
    events.sort((a, b) => {
      const startDiff = dayjs(a.start).diff(b.start);
      if (startDiff !== 0) return startDiff;
      return dayjs(a.end).diff(b.end);
    });

    let targetEvent = events[0];
    for (let i = 1; i < events.length; i++) {
      const nextEvent = events[i];

      const hasOverlap =
        dayjs(nextEvent.end).isAfter(targetEvent.start) &&
        dayjs(nextEvent.start).isBefore(targetEvent.end);

      if (hasOverlap) {
        if (dayjs(nextEvent.end).isAfter(targetEvent.end))
          targetEvent.end = nextEvent.end;
      } else {
        merged.push(targetEvent);
        targetEvent = nextEvent;
      }
    }
    merged.push(targetEvent);
  });

  return merged;
};

export const createAvailabilityEventsFromPlanningEntityResponse = (
  calendarId: string | undefined = '',
  planningEntities: PlanningEntityResponse[]
) => {
  const availabilityEvents: CalendarAvailabilityEvent[] = getAvailabilityEvents(
    calendarId,
    planningEntities
  );

  const mergedEvents = mergeAvailabilityEvents(availabilityEvents);

  return mergedEvents;
};

export const colorForId = (id: string | number) => {
  const hue =
    Math.abs(
      [...String(id)].reduce((h, ch) => (h << 5) - h + ch.charCodeAt(0), 0)
    ) % 360; // full hue spectrum

  return `hsl(${hue}, 70%, 70%)`; // pastel color with fixed saturation & lightness
};

export const calculateOverlapDepth = (
  events: {
    start: string | Date | dayjs.Dayjs;
    end: string | Date | dayjs.Dayjs;
  }[]
) => {
  if (events.length < 2) return events.length;

  const mapped = events.map((ev) => ({
    start: dayjs(ev.start),
    end: dayjs(ev.end)
  }));

  // Each +1 represents an event starting, and each -1 represents
  // and event ending.
  const adjustments: { moment: dayjs.Dayjs; adjustment: 1 | -1 }[] = [];
  mapped.forEach((ev) => {
    adjustments.push({ moment: ev.start, adjustment: 1 });
    adjustments.push({ moment: ev.end, adjustment: -1 });
  });

  // Sort all adjustments by their moment, but also by their adjustment.
  // Events ending takes priority over events starting to ensure perfectly
  // adjacent events aren't considered an overlap.
  adjustments.sort(
    (a, b) => a.moment.diff(b.moment) || a.adjustment - b.adjustment
  );

  // For each adjustment moment, the sum of the adjustments up until that
  // moment represents the concurrent number of events. By checking that
  // value for each event and returning the highest value, we can determine
  // the "deepest" overlap in the set.

  return Math.max(
    ...adjustments.map((_, idx) =>
      adjustments.slice(0, idx).reduce((sum, adj) => sum + adj.adjustment, 0)
    )
  );
};

export const getUniqueEvents = (events: CalendarAppointmentEvent[]) => {
  const eventIds = new Set<string | number>();

  const uniqueEvents = events.filter(({ id }) => {
    if (eventIds.has(id)) return false;

    eventIds.add(id);
    return true;
  });

  return uniqueEvents;
};
